import { FormApi } from 'final-form';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import {
  MarketplaceDatetimeDetails,
  MarketplaceDatetimeFormData,
} from '~admin/view/components/details/marketplace-details/marketplace-datetime-details.component';
import { OfferGroupFormData } from '~admin/view/components/details/offer-group-details/offer-group-fields.component';
import { NumberField } from '~admin/view/components/ui/form/fields/field-number.component';
import { SelectField } from '~admin/view/components/ui/form/fields/field-select.component';
import { TextField } from '~common/components/form/field-text.component';
import {
  TranslationField,
  TranslationFormData,
} from '~admin/view/components/ui/form/fields/field-translation.component';
import { MarketplaceType } from '~client/model/marketplace-type';
import { Box } from '~common/components/box.component';
import { Button } from '~common/components/button/button.component';
import { Size } from '~common/components/button/button.helper';
import { Column } from '~common/components/column.component';
import { optionEnumeratorMapper } from '~common/components/select.helper';
import { Color } from '~common/model/basics';
import { DeleteIcon } from '~web/view/components/ui/icon/delete.icon';

interface Props {
  name: string;
  marketplaceIndex: number;
  onRemove?: (index: number) => void;
  form?: FormApi<EventFormData>;
}

export interface MarketplaceFormData extends MarketplaceDatetimeFormData {
  type: MarketplaceType;
  ticketLimit: number;
  rules: TranslationFormData<string>[];
  lowCapacityPercentageThreshold: number;
  capacity: TranslationFormData<string>[];
  offerGroups: OfferGroupFormData[];
}

export const MarketplaceDetailsFields: React.FunctionComponent<Props> = ({
  name,
  marketplaceIndex,
  onRemove,
  form,
}) => {
  const { t } = useTranslation();

  return (
    <Container>
      <Top>
        <DeleteButton
          color={Color.White}
          size={Size.Small}
          disabled={marketplaceIndex === 0}
          onClick={(): void => {
            onRemove?.(marketplaceIndex);
          }}
        >
          <DeleteIcon color={Color.Black} />
        </DeleteButton>
      </Top>
      <Row>
        <RestrictBox>
          <SelectField
            name={`${name}.type`}
            label={t('createEvent.marketplaceDetails.marketplaceType.label')}
            options={optionEnumeratorMapper(
              MarketplaceType,
              t,
              'createEvent.marketplaceDetails.marketplaceType.enum',
            )}
          />
        </RestrictBox>
      </Row>
      <Row>
        <RestrictBox>
          <NumberField
            name={`${name}.ticketLimit`}
            label={t('createEvent.marketplaceDetails.ticketLimitLabel')}
          />
        </RestrictBox>
      </Row>
      <DividerWrap>
        <Divider />
      </DividerWrap>
      <MarketplaceDatetimeDetails name={name} marketplaceIndex={marketplaceIndex} form={form} />
      <DividerWrap>
        <Divider />
      </DividerWrap>
      <RestrictBox>
        <TranslationField
          name={`${name}.rules`}
          label={t('createEvent.marketplaceDetails.rules')}
          render={(renderProps): ReactNode => <TextField {...renderProps} />}
        />
      </RestrictBox>
      <Row>
        <RestrictBox>
          <NumberField
            name={`${name}.lowCapacityPercentageThreshold`}
            label={t('createEvent.marketplaceDetails.lowCapacityPercentageThreshold')}
            endAdornment={'%'}
          />
        </RestrictBox>
        <RestrictBox>
          <TranslationField
            name={`${name}.capacity`}
            label={t('createEvent.marketplaceDetails.capacity')}
            render={(renderProps): ReactNode => <TextField {...renderProps} />}
          />
        </RestrictBox>
      </Row>
    </Container>
  );
};

const Container = styled(Column)`
  flex: 1 1 auto;
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 18px;
`;

const Row = styled(Box)`
  margin-top: 16px;
`;

const Top = styled(Box)`
  justify-content: flex-end;
`;

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin: 0 12px;
`;

const DividerWrap = styled.div`
  width: 100%;
  padding: 0 12px;
  margin: 28px 0;
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background: ${(props): string => props.theme.colors.gray.v3};
`;

const DeleteButton = styled(Button)`
  && {
    min-width: 44px;
    width: 44px;
    height: 44px;
    padding: 0;
  }
`;



// WEBPACK FOOTER //
// ./view/components/details/marketplace-details/marketplace-details-fields.component.tsx